import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCheck, ArrowLeft, Save, Plus, Trash2, Eye, EyeOff, Sparkles, FileImage, IdCard, Building2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getCemigRule, cemigCommission, clampCemigDiscount, GROUP_A_COMMISSION } from "@/lib/cemigRules";

export default function NewCooperative({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [partners, setPartners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Configurações dinâmicas carregadas do backend
  const [distributorCommissions, setDistributorCommissions] = useState({});
  const [distributorDiscounts, setDistributorDiscounts] = useState({});
  const [autoconsumoDiscounts, setAutoconsumoDiscounts] = useState({});
  
  // Estrutura para múltiplas instalações
  const [installations, setInstallations] = useState([
    {
      installation_number: "",
      installation_class: "",
      contracted_kwh: "",
      street: "",
      number: "",
      complement: "",
      zip_code: "",
      state: "",
      city: "",
      neighborhood: "",
      installation_notes: "",
      energy_bill_file_ref: React.createRef()
    }
  ]);

  const [formData, setFormData] = useState({
    registration_type: "PF",
    name: "",
    email: "",
    phone: "",
    cpf_cnpj: "",
    birth_date: "",
    legal_representative_cpf: "",
    legal_representative_name: "",
    compensation_modality: "Geração Compartilhada",
    consumer_group: "Grupo B",
    group_a_source_simulation_id: "",
    distributor: "",  // Vazio para forçar seleção
    distributor_login: "",
    distributor_password: "",
    generator_installation_number: "",
    agent_number: "",
    partner_id: "",
    partner_commission: "",
    signature_date: "",
    tariff_discount: "",
    contracted_kwh: "",
    rg_cpf_document: "",
    energy_bill_document: "",
    cnpj_card_document: "",  // Para PJ
    origin: "APP",  // Preenchido automaticamente
    situation: "pending"  // Default: Pendente
  });

  const newRgCpfFileRef = useRef(null);
  const newCnpjCardFileRef = useRef(null);

  // ─── IA: preenchimento automático por upload ───
  const [aiExtracting, setAiExtracting] = useState(null); // 'invoice' | 'id' | 'cnpj' | null
  const [aiFilledFields, setAiFilledFields] = useState([]);
  const aiInvoiceRef = useRef(null);
  const aiIdDocRef = useRef(null);
  const aiCnpjRef = useRef(null);

  const _validateAiFile = (file) => {
    const ok = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!ok.includes(file.type)) { toast.error("Envie PDF, JPG, PNG ou WebP."); return false; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Arquivo excede 15 MB."); return false; }
    return true;
  };
  const _applyAiPatch = (patch, source) => {
    const filled = Object.keys(patch);
    if (filled.length === 0) { toast.warning("A IA não identificou nenhum campo — tente uma imagem mais nítida."); return; }
    setFormData((prev) => ({ ...prev, ...patch }));
    setAiFilledFields(filled);
    toast.success(`IA (${source}) preencheu ${filled.length} campo(s). Revise antes de salvar.`);
  };

  const handleAIExtractInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !_validateAiFile(file)) { e.target.value = ""; return; }
    setAiExtracting("invoice");
    const toastId = toast.loading("Lendo fatura com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/cooperatives/ai/extract-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};

      // Campos do formData
      const patch = {};
      const filled = [];
      if (ex.cooperative_name) { patch.name = ex.cooperative_name; filled.push("name"); }
      if (ex.cpf_cnpj) { patch.cpf_cnpj = ex.cpf_cnpj; filled.push("cpf_cnpj"); }
      if (ex.email) { patch.email = ex.email; filled.push("email"); }
      if (ex.phone) { patch.phone = ex.phone; filled.push("phone"); }
      if (ex.birth_date && formData.registration_type === "PF") {
        patch.birth_date = ex.birth_date; filled.push("birth_date");
      }

      // Distribuidora (dispara handleDistributorChange para trazer desconto/comissão do sistema)
      let distMatch = null;
      if (ex.distributor) {
        const distRaw = String(ex.distributor).trim().toUpperCase();
        distMatch = Object.keys(distributorDiscounts).find(
          (d) => d.toUpperCase() === distRaw
        ) || distRaw;
        patch.distributor = distMatch;
        filled.push("distributor");
        const discount = distributorDiscounts[distMatch] || "";
        let commission = distributorCommissions[distMatch] || "";
        if (distMatch === "CEMIG" && discount) commission = cemigCommission(discount, cemigRule);
        if (discount) patch.tariff_discount = String(discount);
        if (commission !== "") patch.partner_commission = String(commission);
      }

      // kW contratado = MÉDIA ANUAL do histórico. Se não houver, usa consumo do mês.
      const hist = Array.isArray(ex.consumption_history)
        ? ex.consumption_history.filter((n) => typeof n === "number" && n > 0)
        : [];
      let annualAvg = null;
      if (hist.length > 0) annualAvg = Math.round(hist.reduce((a, b) => a + b, 0) / hist.length);
      else if (typeof ex.consumption === "number") annualAvg = Math.round(ex.consumption);
      if (annualAvg) { patch.contracted_kwh = String(annualAvg); filled.push("contracted_kwh"); }

      // Aplica patch no formData
      setFormData((prev) => ({ ...prev, ...patch }));

      // Preenche installations[0]: nº instalação, kW contratado e endereço
      if (ex.installation_number) {
        updateInstallation(0, "installation_number", String(ex.installation_number));
        filled.push("installation_number");
      }
      if (annualAvg) {
        updateInstallation(0, "contracted_kwh", String(annualAvg));
      }
      if (ex.address_full) {
        const parsed = _parseAddress(ex.address_full);
        Object.entries(parsed).forEach(([k, v]) => {
          if (v) { updateInstallation(0, k, v); filled.push(k); }
        });
      }

      setAiFilledFields(filled);
      const note = hist.length > 0
        ? ` (kW contratado = média de ${hist.length} meses = ${annualAvg} kWh)`
        : (annualAvg ? " (kW contratado = consumo do mês)" : "");
      if (filled.length === 0) {
        toast.warning("A IA não identificou nenhum campo — tente uma imagem mais nítida.", { id: toastId });
      } else if (note) {
        toast.success(`IA preencheu ${filled.length} campo(s). ${note}`, { id: toastId });
      } else {
        toast.success(`IA preencheu ${filled.length} campo(s).`, { id: toastId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao ler fatura", { id: toastId });
    } finally {
      setAiExtracting(null);
      if (aiInvoiceRef.current) aiInvoiceRef.current.value = "";
    }
  };

  const handleAIExtractIdDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !_validateAiFile(file)) { e.target.value = ""; return; }
    setAiExtracting("id");
    const toastId = toast.loading("Lendo documento com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/cooperatives/ai/extract-id-document", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};
      const patch = {};
      const filled = [];
      if (formData.registration_type === "PJ") {
        // Para PJ, o documento com foto é do REPRESENTANTE LEGAL.
        if (ex.name) { patch.legal_representative_name = ex.name; filled.push("legal_representative_name"); }
        if (ex.cpf) { patch.legal_representative_cpf = ex.cpf; filled.push("legal_representative_cpf"); }
      } else {
        if (ex.name) { patch.name = ex.name; filled.push("name"); }
        if (ex.cpf) { patch.cpf_cnpj = ex.cpf; filled.push("cpf_cnpj"); }
        if (ex.birth_date) { patch.birth_date = ex.birth_date; filled.push("birth_date"); }
      }
      if (filled.length === 0) {
        toast.warning("A IA não identificou nenhum campo — tente uma imagem mais nítida.", { id: toastId });
      } else {
        setFormData((prev) => ({ ...prev, ...patch }));
        setAiFilledFields(filled);
        toast.success(`IA (documento) preencheu ${filled.length} campo(s).`, { id: toastId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao ler documento", { id: toastId });
    } finally {
      setAiExtracting(null);
      if (aiIdDocRef.current) aiIdDocRef.current.value = "";
    }
  };

  const handleAIExtractCnpj = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !_validateAiFile(file)) { e.target.value = ""; return; }
    setAiExtracting("cnpj");
    const toastId = toast.loading("Lendo cartão CNPJ com IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post("/cooperatives/ai/extract-cnpj-card", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const ex = resp?.data?.extracted || {};
      const patch = {};
      const filled = [];
      if (ex.corporate_name) { patch.name = ex.corporate_name; filled.push("name"); }
      if (ex.cnpj) { patch.cpf_cnpj = ex.cnpj; filled.push("cpf_cnpj"); }
      if (ex.email) { patch.email = ex.email; filled.push("email"); }
      if (ex.phone) { patch.phone = ex.phone; filled.push("phone"); }
      if (filled.length === 0) {
        toast.warning("A IA não identificou nenhum campo — tente uma imagem mais nítida.", { id: toastId });
      } else {
        setFormData((prev) => ({ ...prev, ...patch }));
        setAiFilledFields(filled);
        toast.success(`IA (cartão CNPJ) preencheu ${filled.length} campo(s).`, { id: toastId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao ler cartão CNPJ", { id: toastId });
    } finally {
      setAiExtracting(null);
      if (aiCnpjRef.current) aiCnpjRef.current.value = "";
    }
  };

  // Parseia "RUA X, 123, Bairro Y, Cidade/UF, CEP 00000-000" em partes.
  const _parseAddress = (addr) => {
    if (!addr || typeof addr !== "string") return {};
    const out = { street: "", number: "", complement: "", neighborhood: "", city: "", state: "", zip_code: "" };
    // CEP
    const cep = addr.match(/(\d{5})[.\-\s]?(\d{3})/);
    if (cep) out.zip_code = `${cep[1]}-${cep[2]}`;
    // UF: procura por / seguido de 2 letras maiúsculas
    const uf = addr.match(/\/\s*([A-Z]{2})\b/);
    if (uf) out.state = uf[1];
    // Cidade: palavra(s) antes do "/ UF"
    if (uf) {
      const cityRegex = new RegExp(`([A-Za-zÀ-ÿ\\s]+)\\s*\\/\\s*${uf[1]}\\b`);
      const cm = addr.match(cityRegex);
      if (cm) out.city = cm[1].trim();
    }
    // Rua / Número: pega antes da primeira vírgula
    const first = addr.split(",")[0]?.trim() || "";
    const numMatch = first.match(/^(.*?)\s+(SN|S\/N|\d+)/i);
    if (numMatch) {
      out.street = numMatch[1].trim();
      out.number = numMatch[2];
    } else {
      out.street = first;
    }
    // Bairro: 2ª ou 3ª vírgula (aproximado). Tentativa: pega o token entre "Bairro" e a próxima vírgula
    const bMatch = addr.match(/-\s*([A-Za-zÀ-ÿ\s]+)\s+CEP/i);
    if (bMatch) out.neighborhood = bMatch[1].trim();
    return out;
  };

  // Funções para gerenciar instalações
  const addInstallation = () => {
    setInstallations([...installations, {
      installation_number: "",
      installation_class: "",
      contracted_kwh: "",
      street: "",
      number: "",
      complement: "",
      zip_code: "",
      state: "",
      city: "",
      neighborhood: "",
      installation_notes: "",
      energy_bill_file_ref: React.createRef()
    }]);
  };

  const removeInstallation = (index) => {
    if (installations.length > 1) {
      const newInstallations = installations.filter((_, i) => i !== index);
      setInstallations(newInstallations);
    }
  };

  const updateInstallation = (index, field, value) => {
    const newInstallations = [...installations];
    newInstallations[index][field] = value;
    setInstallations(newInstallations);
  };

  useEffect(() => {
    fetchData();
    fetchDistributorSettings();
    
    // Ler dados pré-preenchidos da URL (vindo do simulador)
    const searchParams = new URLSearchParams(location.search);
    const encodedData = searchParams.get('data');
    
    if (encodedData) {
      try {
        let decodedData;
        try {
          decodedData = JSON.parse(decodeURIComponent(escape(atob(encodedData))));
        } catch {
          decodedData = JSON.parse(atob(encodedData));
        }
        const fromGroupA = !!decodedData.group_a_source_simulation_id;
        const distA = (decodedData.distributor || '').toUpperCase();
        
        // Preencher formData com dados da simulação
        setFormData(prev => ({
          ...prev,
          name: decodedData.name || '',
          phone: decodedData.phone || '',
          email: decodedData.email || '',
          cpf_cnpj: decodedData.cpf_cnpj || '',
          registration_type: decodedData.registration_type || 'PF',
          distributor: decodedData.distributor || '',
          tariff_discount: decodedData.tariff_discount || '',
          contracted_kwh: decodedData.contracted_kwh || '',
          ...(fromGroupA ? {
            consumer_group: "Grupo A",
            group_a_source_simulation_id: decodedData.group_a_source_simulation_id,
            compensation_modality: "Autoconsumo Remoto",
            partner_commission: GROUP_A_COMMISSION[distA] != null ? String(GROUP_A_COMMISSION[distA]) : prev.partner_commission,
          } : {}),
        }));
        
        // Preencher primeira instalação
        setInstallations(prev => {
          const newInstallations = [...prev];
          newInstallations[0] = {
            ...newInstallations[0],
            installation_number: decodedData.installation_number || '',
            contracted_kwh: decodedData.contracted_kwh || ''
          };
          return newInstallations;
        });
        
        toast.success("Dados do simulador carregados! Complete o cadastro.");
      } catch (e) {
        console.error("Erro ao decodificar dados:", e);
      }
    }
  }, [location.search]);

  // Preencher automaticamente o parceiro logado (apenas para não-admin)
  useEffect(() => {
    if (partners.length > 0 && user && user.role !== 'admin' && user.role !== 'operador') {
      const userPartner = partners.find(p => p.email === user.email);
      if (userPartner) {
        setFormData(prev => ({
          ...prev,
          partner_id: userPartner.id
        }));
      }
    }
  }, [partners, user]);

  // Preencher formulário com dados passados via navigate state (do Simulador)
  useEffect(() => {
    if (location.state) {
      const {
        name,
        phone,
        distributor,
        installation_number,
        contracted_kwh
      } = location.state;

      // Buscar comissão e desconto da distribuidora
      const discount = distributorDiscounts[distributor] || "";
      let commission = distributorCommissions[distributor] || "";
      
      // Para CEMIG, a comissão depende do desconto (regra por parceiro)
      if (distributor === "CEMIG" && discount) {
        commission = cemigCommission(discount, cemigRule);
      }

      setFormData(prev => ({
        ...prev,
        name: name || "",
        phone: phone || "",
        distributor: distributor || "",
        contracted_kwh: contracted_kwh || "",
        partner_commission: commission,
        tariff_discount: discount
      }));

      // Preencher a primeira instalação
      if (installation_number) {
        updateInstallation(0, 'installation_number', installation_number);
      }
      if (contracted_kwh) {
        updateInstallation(0, 'contracted_kwh', contracted_kwh);
      }

      toast.success("Dados carregados do simulador!");
    }
  }, [location.state]);

  const fetchData = async () => {
    try {
      const response = await axiosInstance.get("/partners");
      const activePartners = response.data.filter(p => p.status === 'active');
      setPartners(activePartners);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    }
  };

  const fetchDistributorSettings = async () => {
    try {
      const res = await axiosInstance.get("/distributor-settings");
      const discounts = {};
      const commissions = {};
      const autoDisc = {};
      for (const item of res.data) {
        discounts[item.distributor] = item.discount;
        commissions[item.distributor] = item.partner_commission;
        autoDisc[item.distributor] = item.autoconsumo_discount || 0;
      }
      setDistributorDiscounts(discounts);
      setDistributorCommissions(commissions);
      setAutoconsumoDiscounts(autoDisc);
    } catch (error) {
      console.error("Erro ao carregar configurações de distribuidoras:", error);
    }
  };


  const handleDistributorChange = (value) => {
    if (!value) return;
    if (isGroupA) {
      toast.info("Cooperado Grupo A: distribuidora definida pela simulação");
      return;
    }
    const isAutoconsumo = (formData.compensation_modality || '').startsWith('Autoconsumo');
    const discount = isAutoconsumo && autoconsumoDiscounts[value]
      ? autoconsumoDiscounts[value]
      : (distributorDiscounts[value] || "");
    let commission = distributorCommissions[value] || "";
    
    if (value === "CEMIG" && discount) {
      commission = cemigCommission(discount, cemigRule);
    }
    
    setFormData({
      ...formData,
      distributor: value,
      tariff_discount: discount,
      partner_commission: commission
    });
  };

  const handleCompensationModalityChange = (value) => {
    if (!value) return;
    const dist = formData.distributor;
    let discount = formData.tariff_discount;
    if (dist) {
      discount = (value || '').startsWith('Autoconsumo') && autoconsumoDiscounts[dist]
        ? autoconsumoDiscounts[dist]
        : (distributorDiscounts[dist] || discount);
    }
    setFormData({
      ...formData,
      compensation_modality: value,
      tariff_discount: discount
    });
  };

  const selectedPartner = partners.find(p => p.id === formData.partner_id);
  const cemigRule = getCemigRule(user?.email, selectedPartner?.email);
  const isGroupA = formData.consumer_group === "Grupo A";
  const isCemig = formData.distributor === 'CEMIG';

  const handleConsumerGroupChange = (value) => {
    if (!value) return;
    if (value === "Grupo A" && !formData.group_a_source_simulation_id) {
      toast.error("Cadastro Grupo A só é permitido a partir do Simulador Grupo A (botão \"Cadastrar como cooperado\").");
      return;
    }
    setFormData({ ...formData, consumer_group: value });
  };

  const handleCemigDiscountBlur = () => {
    if (!isCemig || isGroupA || user?.role === 'admin') return;
    const fixed = clampCemigDiscount(formData.tariff_discount, cemigRule);
    if (fixed !== null) {
      toast.warning(`Desconto CEMIG deve ficar entre ${cemigRule.min}% e ${cemigRule.max}%. Ajustado para ${fixed}%.`);
      setFormData({ ...formData, tariff_discount: String(fixed), partner_commission: cemigCommission(fixed, cemigRule) });
    }
  };

  const handleDiscountChange = (discountValue) => {
    if (isGroupA) return;
    // CEMIG: a comissão depende do desconto (regra por parceiro)
    if (formData.distributor === 'CEMIG' && discountValue !== '') {
      const commission = cemigCommission(discountValue || 0, cemigRule);
      setFormData({
        ...formData,
        tariff_discount: discountValue,
        partner_commission: commission
      });
      return;
    }
    // Para todas as outras distribuidoras (admin pode editar livremente),
    // apenas atualiza o desconto sem recalcular comissão.
    setFormData({
      ...formData,
      tariff_discount: discountValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    // Declared OUTSIDE try so the catch block can read it (fixes
    // "ReferenceError: successCount is not defined" when the first
    // installation fails).
    let successCount = 0;

    try {
      // Validar se cada instalação tem sua fatura
      for (let i = 0; i < installations.length; i++) {
        if (!installations[i].energy_bill_file_ref.current?.files[0]) {
          toast.error(`Fatura de energia da instalação ${i + 1} é obrigatória`);
          setSaving(false);
          return;
        }
      }

      // Validar documentos compartilhados
      if (!newRgCpfFileRef.current?.files[0]) {
        toast.error("RG/CPF é obrigatório");
        setSaving(false);
        return;
      }
      if (formData.registration_type === "PJ" && !newCnpjCardFileRef.current?.files[0]) {
        toast.error("Cartão CNPJ é obrigatório para Pessoa Jurídica");
        setSaving(false);
        return;
      }

      // Validar kWh mínimo para todas as instalações
      for (let i = 0; i < installations.length; i++) {
        const kwh = parseInt(installations[i].contracted_kwh, 10);
        if (isNaN(kwh) || kwh < 200) {
          toast.error(`kWh Contratado da instalação ${i + 1} deve ser no mínimo 200 kWh (apenas inteiros)`);
          setSaving(false);
          return;
        }
      }

      // Enviar cada instalação como um cooperado separado
      // (successCount declared above outside the try)
      
      for (let i = 0; i < installations.length; i++) {
        const installation = installations[i];
        const submitData = new FormData();

        console.log(`📝 Cadastrando instalação ${i + 1}/${installations.length}`);
        
        // Campos básicos
        submitData.append("registration_type", formData.registration_type);
        submitData.append("name", formData.name);
        submitData.append("email", formData.email);
        submitData.append("phone", formData.phone);
        submitData.append("cpf_cnpj", formData.cpf_cnpj);
        if (formData.birth_date) submitData.append("birth_date", formData.birth_date);
        if (formData.legal_representative_cpf) submitData.append("legal_representative_cpf", formData.legal_representative_cpf);
        if (formData.legal_representative_name) submitData.append("legal_representative_name", formData.legal_representative_name);

        // Dados da instalação
        submitData.append("compensation_modality", formData.compensation_modality);
        submitData.append("consumer_group", formData.consumer_group || "Grupo B");
        if (formData.group_a_source_simulation_id) submitData.append("group_a_source_simulation_id", formData.group_a_source_simulation_id);
        submitData.append("distributor", formData.distributor);
        if (formData.distributor_login) submitData.append("distributor_login", formData.distributor_login);
        if (formData.distributor_password) submitData.append("distributor_password", formData.distributor_password);
        submitData.append("installation_number", installation.installation_number);
        if (installation.installation_class) submitData.append("installation_class", installation.installation_class);
        submitData.append("contracted_kwh", installation.contracted_kwh || formData.contracted_kwh);
        submitData.append("tariff_discount", formData.tariff_discount);
        submitData.append("partner_commission", formData.partner_commission);
        submitData.append("partner_id", formData.partner_id);
        
        // Endereço desta instalação
        submitData.append("street", installation.street);
        submitData.append("number", installation.number);
        if (installation.complement) submitData.append("complement", installation.complement);
        submitData.append("neighborhood", installation.neighborhood);
        submitData.append("city", installation.city);
        submitData.append("state", installation.state);
        submitData.append("zip_code", installation.zip_code);
        
        // Observações
        if (installation.installation_notes) submitData.append("installation_notes", installation.installation_notes);
        
        // Situação sempre pendente
        submitData.append("situation", "pending");
        submitData.append("origin", "APP");

        // Documentos compartilhados (enviados em todas as instalações)
        submitData.append("rg_cpf_file", newRgCpfFileRef.current.files[0]);
        if (formData.registration_type === "PJ") {
          submitData.append("cnpj_card_file", newCnpjCardFileRef.current.files[0]);
        }

        // Fatura específica desta instalação
        submitData.append("energy_bill_file", installation.energy_bill_file_ref.current.files[0]);

        console.log(`✉️ Enviando dados da instalação ${i + 1}:`, {
          name: formData.name,
          installation_number: installation.installation_number,
          contracted_kwh: installation.contracted_kwh || formData.contracted_kwh,
          street: installation.street,
          city: installation.city
        });

        await axiosInstance.post("/cooperatives", submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log(`✅ Instalação ${i + 1} cadastrada com sucesso`);
        successCount++;
      }

      console.log(`🎉 Total de ${successCount} cooperado(s) cadastrado(s)`);
      toast.success(`${successCount} cooperado(s) cadastrado(s) com sucesso!`);
      navigate("/cooperatives");
      
    } catch (error) {
      console.error("❌ Erro ao cadastrar cooperado:", error);
      console.error("Detalhes do erro:", error.response?.data);
      
      const errorMessage = error.response?.data?.detail || error.message || "Erro ao cadastrar cooperado";
      const errorMsg = typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage);
      
      toast.error(`Erro: ${errorMsg}`);
      
      // Se alguma instalação foi cadastrada com sucesso antes do erro
      if (successCount > 0) {
        toast.info(`${successCount} instalação(ões) foi(ram) cadastrada(s) antes do erro`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/cooperatives")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800">Novo Cooperado</h1>
            <p className="text-gray-600">Preencha os dados para realizar o cadastro no sistema.</p>
          </div>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ─── Bloco: Preenchimento automático com IA ─── */}
          <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 via-fuchsia-50 to-pink-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-700" />
                <span className="text-sm font-semibold text-purple-800">Preenchimento automático com IA</span>
                <span className="text-[10px] px-2 py-0.5 bg-purple-200 text-purple-900 rounded-full">Gemini 3 Flash</span>
              </div>
              <p className="text-[11px] text-purple-700/80 mb-3">
                Envie a fatura, um documento com foto (RG/CNH) ou o Cartão CNPJ (PJ) — a IA lê e preenche os campos automaticamente. <br />
                <strong>Fatura:</strong> o campo <em>kW contratado</em> é preenchido com a <strong>média anual do histórico de consumo</strong>.
              </p>
              <input ref={aiInvoiceRef} type="file" accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleAIExtractInvoice} data-testid="coop-ai-invoice-input" />
              <input ref={aiIdDocRef} type="file" accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleAIExtractIdDoc} data-testid="coop-ai-id-input" />
              <input ref={aiCnpjRef} type="file" accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleAIExtractCnpj} data-testid="coop-ai-cnpj-input" />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => aiInvoiceRef.current?.click()}
                  disabled={aiExtracting !== null}
                  data-testid="coop-ai-invoice-btn"
                  className="bg-white hover:bg-purple-50 text-purple-800 border border-purple-300 h-9 px-3 text-xs"
                >
                  <FileImage className="w-3.5 h-3.5 mr-1.5" />
                  {aiExtracting === "invoice" ? "Lendo fatura…" : "Ler Fatura"}
                </Button>
                <Button
                  type="button"
                  onClick={() => aiIdDocRef.current?.click()}
                  disabled={aiExtracting !== null}
                  data-testid="coop-ai-id-btn"
                  className="bg-white hover:bg-purple-50 text-purple-800 border border-purple-300 h-9 px-3 text-xs"
                  title={formData.registration_type === "PJ"
                    ? "Documento com foto do REPRESENTANTE LEGAL (PJ)"
                    : "Envie RG, CNH ou CIN do titular"}
                >
                  <IdCard className="w-3.5 h-3.5 mr-1.5" />
                  {aiExtracting === "id"
                    ? "Lendo documento…"
                    : (formData.registration_type === "PJ" ? "Ler Doc. Representante" : "Ler Documento c/ Foto")}
                </Button>
                {formData.registration_type === "PJ" && (
                  <Button
                    type="button"
                    onClick={() => aiCnpjRef.current?.click()}
                    disabled={aiExtracting !== null}
                    data-testid="coop-ai-cnpj-btn"
                    className="bg-white hover:bg-purple-50 text-purple-800 border border-purple-300 h-9 px-3 text-xs"
                  >
                    <Building2 className="w-3.5 h-3.5 mr-1.5" />
                    {aiExtracting === "cnpj" ? "Lendo cartão CNPJ…" : "Ler Cartão CNPJ"}
                  </Button>
                )}
              </div>
              {aiFilledFields.length > 0 && (
                <div className="mt-3 text-[11px] text-purple-800 font-medium" data-testid="coop-ai-filled-summary">
                  ✨ IA preencheu {aiFilledFields.length} campo(s): {aiFilledFields.slice(0, 8).join(", ")}{aiFilledFields.length > 8 ? "…" : ""} — revise antes de salvar.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tipo de Cadastro */}
          <Card>
            <CardHeader>
              <CardTitle>Tipo de Cadastro</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={formData.registration_type} onValueChange={(value) => setFormData({ ...formData, registration_type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PF">Pessoa Física</SelectItem>
                  <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Dados Básicos */}
          <Card>
            <CardHeader>
              <CardTitle>Dados Básicos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Cooperado *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>E-mail *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Telefone *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>CPF/CNPJ *</Label>
                  <Input value={formData.cpf_cnpj} onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Data de Nascimento</Label>
                  <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} />
                </div>
              </div>

              {/* Campos PJ */}
              {formData.registration_type === "PJ" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>CPF do Representante Legal</Label>
                    <Input value={formData.legal_representative_cpf} onChange={(e) => setFormData({ ...formData, legal_representative_cpf: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome do Representante Legal</Label>
                    <Input value={formData.legal_representative_name} onChange={(e) => setFormData({ ...formData, legal_representative_name: e.target.value })} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dados Gerais da Instalação */}
          <Card>
            <CardHeader>
              <CardTitle>Dados Gerais da Instalação</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Modalidade de Compensação *</Label>
                  <Select value={formData.compensation_modality} onValueChange={handleCompensationModalityChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Geração Compartilhada">Geração Compartilhada</SelectItem>
                      <SelectItem value="Autoconsumo Remoto">Autoconsumo Remoto</SelectItem>
                      <SelectItem value="Autoconsumo C">Autoconsumo C</SelectItem>
                      {user?.role === 'admin' && (
                        <SelectItem value="Autoconsumo Remoto - Usina">Autoconsumo Remoto - Usina</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Distribuidora *</Label>
                  <Select value={formData.distributor} onValueChange={handleDistributorChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(distributorDiscounts).sort().map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.distributor && (
                    <p className="text-xs text-gray-500">
                      {isGroupA
                        ? `Grupo A: comissão fixa ${GROUP_A_COMMISSION[formData.distributor.toUpperCase()] ?? '-'}%`
                        : formData.distributor === 'CEMIG' 
                        ? `Comissão = ${cemigRule.max} − Desconto (%) · faixa ${cemigRule.min}–${cemigRule.max}%`
                        : 'Comissão e desconto automáticos'}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Grupo *</Label>
                  <Select value={formData.consumer_group} onValueChange={handleConsumerGroupChange} disabled={isGroupA}>
                    <SelectTrigger data-testid="new-coop-consumer-group" className={isGroupA ? 'bg-gray-100' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Grupo B">Grupo B</SelectItem>
                      <SelectItem value="Grupo A">Grupo A</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {isGroupA
                      ? 'Definido pela simulação do Grupo A (desconto e comissão travados)'
                      : 'Grupo A somente via Simulador Grupo A → "Cadastrar como cooperado"'}
                  </p>
                </div>
                {formData.distributor && formData.distributor !== 'CEMIG' && (
                  <>
                    <div className="space-y-2">
                      <Label>Login da Distribuidora *</Label>
                      <Input value={formData.distributor_login} onChange={(e) => setFormData({ ...formData, distributor_login: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Senha da Distribuidora *</Label>
                      <div className="relative">
                        <Input 
                          type={showPassword ? "text" : "password"} 
                          value={formData.distributor_password} 
                          onChange={(e) => setFormData({ ...formData, distributor_password: e.target.value })} 
                          required 
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Parceiro e Comissão */}
          <Card>
            <CardHeader>
              <CardTitle>Parceiro e Comissão</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Parceiro Comercial *</Label>
                  <Select 
                    value={formData.partner_id} 
                    onValueChange={(value) => setFormData({ ...formData, partner_id: value })} 
                    disabled={user?.role !== 'admin' && user?.role !== 'operador'}
                  >
                    <SelectTrigger className={(user?.role !== 'admin' && user?.role !== 'operador') ? 'bg-gray-100' : ''}>
                      <SelectValue placeholder="Selecione o parceiro" />
                    </SelectTrigger>
                    <SelectContent>
                      {partners.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(user?.role !== 'admin' && user?.role !== 'operador') && (
                    <p className="text-xs text-gray-500">Preenchido automaticamente com seu parceiro</p>
                  )}
                  {(user?.role === 'admin' || user?.role === 'operador') && (
                    <p className="text-xs text-blue-600">Selecione o parceiro responsável pelo cooperado</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Comissão do Parceiro (%) *</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={formData.partner_commission} 
                    onChange={(e) => setFormData({...formData, partner_commission: e.target.value})}
                    className={(user?.role !== 'admin' || isGroupA) ? "bg-gray-100 cursor-not-allowed" : ""} 
                    readOnly={user?.role !== 'admin' || isGroupA} 
                    required 
                  />
                  <p className="text-xs text-gray-500">
                    {isGroupA
                      ? 'Grupo A: comissão fixa por distribuidora (CELESC 2% · CEMIG 5% · COPEL 4% · RGE 5%)'
                      : user?.role === 'admin'
                      ? '🔓 Admin: Comissão editável para todas as distribuidoras'
                      : formData.distributor === 'CEMIG' 
                      ? `Calculada como: ${cemigRule.max} − Desconto (%)` 
                      : 'Definida automaticamente pela distribuidora'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contrato */}
          <Card>
            <CardHeader>
              <CardTitle>Dados do Contrato</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Desconto na Tarifa (%) *</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={formData.tariff_discount} 
                    onChange={(e) => handleDiscountChange(e.target.value)} 
                    onBlur={handleCemigDiscountBlur}
                    readOnly={isGroupA || (formData.distributor !== 'CEMIG' && user?.role !== 'admin')}
                    className={(isGroupA || (formData.distributor !== 'CEMIG' && user?.role !== 'admin')) ? 'bg-gray-100 cursor-not-allowed' : ''}
                    data-testid="new-coop-discount"
                    required 
                  />
                  <p className="text-xs text-gray-500" data-testid="new-coop-discount-hint">
                    {isGroupA
                      ? 'Grupo A: desconto calculado na simulação (travado)'
                      : user?.role === 'admin'
                      ? '🔓 Admin: Desconto editável para todas as distribuidoras'
                      : formData.distributor === 'CEMIG' 
                      ? `Editável entre ${cemigRule.min}% e ${cemigRule.max}% - a comissão é recalculada automaticamente` 
                      : 'Automático - Somente editável para CEMIG'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 italic mt-4">
                📅 A data de assinatura será registrada automaticamente quando o admin ativar o cooperado.
              </p>
              <p className="text-xs text-blue-600 italic mt-2">
                💡 O KWh Contratado é definido individualmente para cada instalação abaixo.
              </p>
            </CardContent>
          </Card>

          {/* Instalação Principal */}
          <Card>
            <CardHeader>
              <CardTitle>Dados da Instalação Principal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número da Instalação (UC) *</Label>
                  <Input
                    value={installations[0].installation_number}
                    onChange={(e) => updateInstallation(0, 'installation_number', e.target.value)}
                    required
                    placeholder="Ex: 123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Classe da Instalação</Label>
                  <Select
                    value={installations[0].installation_class}
                    onValueChange={(value) => updateInstallation(0, 'installation_class', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Comercial Monofásico">Comercial Monofásico</SelectItem>
                      <SelectItem value="Residencial Monofásico">Residencial Monofásico</SelectItem>
                      <SelectItem value="Industrial Monofásico">Industrial Monofásico</SelectItem>
                      <SelectItem value="Poder Público Monofásico">Poder Público Monofásico</SelectItem>
                      <SelectItem value="Rural Monofásico">Rural Monofásico</SelectItem>
                      <SelectItem value="Comercial Bifásico">Comercial Bifásico</SelectItem>
                      <SelectItem value="Residencial Bifásico">Residencial Bifásico</SelectItem>
                      <SelectItem value="Industrial Bifásico">Industrial Bifásico</SelectItem>
                      <SelectItem value="Poder Público Bifásico">Poder Público Bifásico</SelectItem>
                      <SelectItem value="Rural Bifásico">Rural Bifásico</SelectItem>
                      <SelectItem value="Comercial Trifásico">Comercial Trifásico</SelectItem>
                      <SelectItem value="Residencial Trifásico">Residencial Trifásico</SelectItem>
                      <SelectItem value="Industrial Trifásico">Industrial Trifásico</SelectItem>
                      <SelectItem value="Poder Público Trifásico">Poder Público Trifásico</SelectItem>
                      <SelectItem value="Rural Trifásico">Rural Trifásico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>kWh Contratado *</Label>
                  <Input
                    type="number"
                    step="1"
                    min="200"
                    value={installations[0].contracted_kwh}
                    onChange={(e) => updateInstallation(0, 'contracted_kwh', Math.floor(e.target.value) || '')}
                    required
                    placeholder="Mínimo 200"
                  />
                  <p className="text-xs text-gray-500">Mínimo: 200 kWh (apenas inteiros)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rua *</Label>
                  <Input
                    value={installations[0].street}
                    onChange={(e) => updateInstallation(0, 'street', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Número *</Label>
                  <Input
                    value={installations[0].number}
                    onChange={(e) => updateInstallation(0, 'number', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Complemento</Label>
                  <Input
                    value={installations[0].complement}
                    onChange={(e) => updateInstallation(0, 'complement', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>CEP *</Label>
                  <Input
                    value={installations[0].zip_code}
                    onChange={(e) => updateInstallation(0, 'zip_code', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>UF *</Label>
                  <Select
                    value={installations[0].state}
                    onValueChange={(value) => updateInstallation(0, 'state', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AC">AC</SelectItem>
                      <SelectItem value="AL">AL</SelectItem>
                      <SelectItem value="AP">AP</SelectItem>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="BA">BA</SelectItem>
                      <SelectItem value="CE">CE</SelectItem>
                      <SelectItem value="DF">DF</SelectItem>
                      <SelectItem value="ES">ES</SelectItem>
                      <SelectItem value="GO">GO</SelectItem>
                      <SelectItem value="MA">MA</SelectItem>
                      <SelectItem value="MT">MT</SelectItem>
                      <SelectItem value="MS">MS</SelectItem>
                      <SelectItem value="MG">MG</SelectItem>
                      <SelectItem value="PA">PA</SelectItem>
                      <SelectItem value="PB">PB</SelectItem>
                      <SelectItem value="PR">PR</SelectItem>
                      <SelectItem value="PE">PE</SelectItem>
                      <SelectItem value="PI">PI</SelectItem>
                      <SelectItem value="RJ">RJ</SelectItem>
                      <SelectItem value="RN">RN</SelectItem>
                      <SelectItem value="RS">RS</SelectItem>
                      <SelectItem value="RO">RO</SelectItem>
                      <SelectItem value="RR">RR</SelectItem>
                      <SelectItem value="SC">SC</SelectItem>
                      <SelectItem value="SP">SP</SelectItem>
                      <SelectItem value="SE">SE</SelectItem>
                      <SelectItem value="TO">TO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cidade *</Label>
                  <Input
                    value={installations[0].city}
                    onChange={(e) => updateInstallation(0, 'city', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bairro *</Label>
                  <Input
                    value={installations[0].neighborhood}
                    onChange={(e) => updateInstallation(0, 'neighborhood', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Observações desta Instalação</Label>
                <Textarea
                  value={installations[0].installation_notes}
                  onChange={(e) => updateInstallation(0, 'installation_notes', e.target.value)}
                  rows={2}
                  placeholder="Observações específicas desta instalação"
                />
              </div>

              {/* Upload de fatura da instalação principal */}
              <div className="space-y-2 border-t pt-4">
                <Label htmlFor="energy-bill-0" className="text-blue-700 font-semibold">
                  Cópia da Fatura de Energia <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="energy-bill-0"
                  ref={installations[0].energy_bill_file_ref}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="cursor-pointer"
                  required
                />
                <p className="text-xs text-gray-500">PDF, JPG ou PNG (obrigatório)</p>
              </div>
            </CardContent>
          </Card>

          {/* Instalações Adicionais (apenas se houver mais de 1) */}
          {installations.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Instalações Adicionais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {installations.slice(1).map((installation, idx) => {
                  const index = idx + 1; // índice real no array
                  return (
                    <Card key={index} className="border-2 border-blue-100 bg-blue-50/30">
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold text-blue-700">Instalação {index + 1}</h4>
                            <Button
                              type="button"
                              onClick={() => removeInstallation(index)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remover
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Número da Instalação (UC) *</Label>
                              <Input
                                value={installation.installation_number}
                                onChange={(e) => updateInstallation(index, 'installation_number', e.target.value)}
                                required
                                placeholder="Ex: 123456789"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Classe da Instalação</Label>
                              <Select
                                value={installation.installation_class}
                                onValueChange={(value) => updateInstallation(index, 'installation_class', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Comercial Monofásico">Comercial Monofásico</SelectItem>
                                  <SelectItem value="Residencial Monofásico">Residencial Monofásico</SelectItem>
                                  <SelectItem value="Industrial Monofásico">Industrial Monofásico</SelectItem>
                                  <SelectItem value="Poder Público Monofásico">Poder Público Monofásico</SelectItem>
                                  <SelectItem value="Rural Monofásico">Rural Monofásico</SelectItem>
                                  <SelectItem value="Comercial Bifásico">Comercial Bifásico</SelectItem>
                                  <SelectItem value="Residencial Bifásico">Residencial Bifásico</SelectItem>
                                  <SelectItem value="Industrial Bifásico">Industrial Bifásico</SelectItem>
                                  <SelectItem value="Poder Público Bifásico">Poder Público Bifásico</SelectItem>
                                  <SelectItem value="Rural Bifásico">Rural Bifásico</SelectItem>
                                  <SelectItem value="Comercial Trifásico">Comercial Trifásico</SelectItem>
                                  <SelectItem value="Residencial Trifásico">Residencial Trifásico</SelectItem>
                                  <SelectItem value="Industrial Trifásico">Industrial Trifásico</SelectItem>
                                  <SelectItem value="Poder Público Trifásico">Poder Público Trifásico</SelectItem>
                                  <SelectItem value="Rural Trifásico">Rural Trifásico</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>kWh Contratado *</Label>
                              <Input
                                type="number"
                                step="1"
                                min="200"
                                value={installation.contracted_kwh}
                                onChange={(e) => updateInstallation(index, 'contracted_kwh', Math.floor(e.target.value) || '')}
                                required
                                placeholder="Mínimo 200"
                              />
                              <p className="text-xs text-gray-500">Mínimo: 200 kWh</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Rua *</Label>
                              <Input
                                value={installation.street}
                                onChange={(e) => updateInstallation(index, 'street', e.target.value)}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Número *</Label>
                              <Input
                                value={installation.number}
                                onChange={(e) => updateInstallation(index, 'number', e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Complemento</Label>
                              <Input
                                value={installation.complement}
                                onChange={(e) => updateInstallation(index, 'complement', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>CEP *</Label>
                              <Input
                                value={installation.zip_code}
                                onChange={(e) => updateInstallation(index, 'zip_code', e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label>UF *</Label>
                              <Select
                                value={installation.state}
                                onValueChange={(value) => updateInstallation(index, 'state', value)}
                                required
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="UF" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="AC">AC</SelectItem>
                                  <SelectItem value="AL">AL</SelectItem>
                                  <SelectItem value="AP">AP</SelectItem>
                                  <SelectItem value="AM">AM</SelectItem>
                                  <SelectItem value="BA">BA</SelectItem>
                                  <SelectItem value="CE">CE</SelectItem>
                                  <SelectItem value="DF">DF</SelectItem>
                                  <SelectItem value="ES">ES</SelectItem>
                                  <SelectItem value="GO">GO</SelectItem>
                                  <SelectItem value="MA">MA</SelectItem>
                                  <SelectItem value="MT">MT</SelectItem>
                                  <SelectItem value="MS">MS</SelectItem>
                                  <SelectItem value="MG">MG</SelectItem>
                                  <SelectItem value="PA">PA</SelectItem>
                                  <SelectItem value="PB">PB</SelectItem>
                                  <SelectItem value="PR">PR</SelectItem>
                                  <SelectItem value="PE">PE</SelectItem>
                                  <SelectItem value="PI">PI</SelectItem>
                                  <SelectItem value="RJ">RJ</SelectItem>
                                  <SelectItem value="RN">RN</SelectItem>
                                  <SelectItem value="RS">RS</SelectItem>
                                  <SelectItem value="RO">RO</SelectItem>
                                  <SelectItem value="RR">RR</SelectItem>
                                  <SelectItem value="SC">SC</SelectItem>
                                  <SelectItem value="SP">SP</SelectItem>
                                  <SelectItem value="SE">SE</SelectItem>
                                  <SelectItem value="TO">TO</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Cidade *</Label>
                              <Input
                                value={installation.city}
                                onChange={(e) => updateInstallation(index, 'city', e.target.value)}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Bairro *</Label>
                              <Input
                                value={installation.neighborhood}
                                onChange={(e) => updateInstallation(index, 'neighborhood', e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>Observações desta Instalação</Label>
                            <Textarea
                              value={installation.installation_notes}
                              onChange={(e) => updateInstallation(index, 'installation_notes', e.target.value)}
                              rows={2}
                              placeholder="Observações específicas desta instalação"
                            />
                          </div>

                          {/* Upload de fatura específica desta instalação */}
                          <div className="space-y-2 border-t pt-4">
                            <Label htmlFor={`energy-bill-${index}`} className="text-blue-700 font-semibold">
                              Cópia da Fatura de Energia desta UC <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id={`energy-bill-${index}`}
                              ref={installation.energy_bill_file_ref}
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="cursor-pointer"
                              required
                            />
                            <p className="text-xs text-gray-500">PDF, JPG ou PNG (obrigatório para cada UC)</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Botão para adicionar instalação */}
          <div className="flex justify-center">
            <Button
              type="button"
              onClick={addInstallation}
              variant="outline"
              size="lg"
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              <Plus className="w-5 h-5 mr-2" />
              Adicionar Instalação
            </Button>
          </div>

          {/* Observações e Controle */}
          <Card>
            <CardHeader>
              <CardTitle>Observações e Controle</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Observações da Instalação</Label>
                  <Textarea value={formData.installation_notes} onChange={(e) => setFormData({ ...formData, installation_notes: e.target.value })} rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>Situação *</Label>
                  <Select value={formData.situation} onValueChange={(value) => setFormData({ ...formData, situation: value })} disabled>
                    <SelectTrigger className="bg-gray-100">
                      <SelectValue placeholder="Pendente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="sent_for_signature">Enviado para Assinatura</SelectItem>
                      <SelectItem value="active">Ativo</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Será definido como "Pendente". Apenas admin pode alterar.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documentos Obrigatórios */}
          <Card>
            <CardHeader>
              <CardTitle>Documentos Obrigatórios</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-rg-cpf-file">
                      Cópia de RG/CPF <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="new-rg-cpf-file"
                      ref={newRgCpfFileRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="cursor-pointer"
                      required
                    />
                    <p className="text-xs text-gray-500">PDF, JPG ou PNG (obrigatório)</p>
                  </div>
                  
                  {formData.registration_type === "PJ" && (
                    <div className="space-y-2">
                      <Label htmlFor="new-cnpj-card-file">
                        Cópia de Cartão CNPJ <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="new-cnpj-card-file"
                        ref={newCnpjCardFileRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="cursor-pointer"
                        required
                      />
                      <p className="text-xs text-gray-500">PDF, JPG ou PNG (obrigatório para PJ)</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Button type="submit" disabled={saving} className="w-full bg-gradient-to-r from-blue-500 to-cyan-600">
            {saving ? "Cadastrando..." : "Cadastrar Cooperado"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
